<template>

  <report-panel icon="$kpi" :title="$t('titles.key_performance_indicators_abbr')" :loading="loading">

    <v-row>

      <v-col cols="12" sm="6" md="4" lg="3" xl="2">
        <kpi-tile
          :title="$t('titles.total_gross_revenue')"
          :current="kpis?.current?.total_gross_revenue"
          :previous="kpis?.previous?.total_gross_revenue"
          format="currency"
        />
      </v-col>

      <v-col cols="12" sm="6" md="4" lg="3" xl="2">
        <kpi-tile
          :title="$t('titles.total_net_revenue')"
          :current="kpis?.current?.total_net_revenue"
          :previous="kpis?.previous?.total_net_revenue"
          format="currency"
        />
      </v-col>

      <v-col cols="12" sm="6" md="4" lg="3" xl="2">
        <kpi-tile
          :title="$t('titles.total_commission')"
          :current="currentTotalCommission"
          :previous="previousTotalCommission"
          format="currency"
        />
      </v-col>

      <!--
      <v-col cols="12" sm="6" md="4" lg="3" xl="2">
        <kpi-tile
          :title="$t('titles.ocra_generated_revenue')"
          :current="kpis?.current?.ocra_revenue"
          :previous="kpis?.previous?.ocra_revenue"
          format="currency"
        />
        <p class="text-error">TODO: Chris says we may need to hide this tile for now. (<a href="https://ocra-hq.slack.com/archives/D03C1U484R1/p1705953751711109?thread_ts=1705949168.312889&cid=D03C1U484R1">Slack discussion</a>)</p>
      </v-col>
      -->

      <v-col cols="12" sm="6" md="4" lg="3" xl="2">
        <kpi-tile
          :title="$t('titles.average_rate')"
          :current="kpis?.current?.average_rate"
          :previous="kpis?.previous?.average_rate"
          format="currency"
        />
      </v-col>

      <v-col cols="12" sm="6" md="4" lg="3" xl="2">
        <kpi-tile
          :title="$t('titles.total_transactions')"
          :current="kpis?.current?.total_transactions"
          :previous="kpis?.previous?.total_transactions"
          format="integer"
        />
      </v-col>

      <v-col cols="12" sm="6" md="4" lg="3" xl="2">
        <kpi-tile
          :title="$t('titles.average_daily_transactions')"
          :current="kpis?.current?.average_daily_transactions"
          :previous="kpis?.previous?.average_daily_transactions"
          format="decimal"
          :precision="2"
        />
      </v-col>

      <v-col cols="12" sm="6" md="4" lg="3" xl="2">
        <kpi-tile
          :title="$t('titles.cancellation_rate')"
          :current="kpis?.current?.cancellation_rate"
          :previous="kpis?.previous?.cancellation_rate"
          format="percentage"
          good-delta="lower"
        />
      </v-col>

      <v-col cols="12" sm="6" md="4" lg="3" xl="2">
        <kpi-tile
          :title="$t('titles.average_occupancy')"
          :current="kpis?.current?.average_occupancy"
          :previous="kpis?.previous?.average_occupancy"
          format="percentage"
        />
      </v-col>

      <v-col cols="12" sm="6" md="4" lg="3" xl="2">
        <kpi-tile
          :title="$t('titles.median_duration')"
          :current="kpis?.current?.median_duration ? (kpis.current.median_duration > 48 ? kpis.current.median_duration / 24 : kpis.current.median_duration) : null"
          :previous="kpis?.previous?.median_duration ? (kpis.previous.median_duration > 48 ? kpis.previous.median_duration / 24 : kpis.previous.median_duration) : null"
          :format="kpis?.current?.median_duration && kpis?.current?.median_duration > 48 ? 'duration_days' : 'duration_hours'"
          :precision="2"
          good-delta="none"
        />
      </v-col>

      <v-col cols="12" sm="6" md="4" lg="3" xl="2">
        <kpi-tile
          :title="$t('titles.advanced_booking_window')"
          :current="kpis?.current?.median_booking_window"
          :previous="kpis?.previous?.median_booking_window"
          format="duration_days"
          good-delta="none"
        />
      </v-col>

      <v-col cols="12" sm="6" md="4" lg="3" xl="2">
        <kpi-tile
          :title="$t('titles.revpas')"
          :current="kpis?.current?.revpas"
          :previous="kpis?.previous?.revpas"
          format="currency"
        />
      </v-col>

    </v-row>

  </report-panel>

</template>

<script setup lang="ts">

import { computed, ref } from 'vue'
import { computedAsync } from '@vueuse/core'
import ReportPanel from './ReportPanel.vue'
import KpiTile from './KpiTile.vue'
import NotificationService from '../../../../services/NotificationService'
import { ReportingApi, type GetKpisResponse, type KpiPeriod } from '../../../../services/api-clients/ReportingApi'
import { Temporal } from '@js-temporal/polyfill'

interface Props {
  locationIds: number[]
  channels: string[]
  start: null | Temporal.PlainDate
  end: null | Temporal.PlainDate
}

const props = withDefaults(defineProps<Props>(), {
  channels: () => [],
})

const loading = ref<boolean>(false)
// TODO: allow cancellation

const kpis = computedAsync<null | GetKpisResponse>(

  async () => {

    if (
      props.locationIds.length === 0 ||
      !props.start ||
      !props.end
    ) {
      return null
    }

    loading.value = true
    let output = null

    await ReportingApi.getKPIs({
      locationIds: props.locationIds,
      channels: props.channels,
      start: props.start,
      end: props.end,
    })
    .then(response => {
      output = response
    })
    .catch((error) => {
      console.error(error)
      NotificationService.error('Couldn\'t load KPIs!')
    })
    .finally(() => {
      loading.value = false
    })

    return output

  },

  null // default value

)

const currentTotalCommission = computed<number | null>(() => {
  return kpis.value ? calculateTotalCommission(kpis.value.current) : null
})

const previousTotalCommission = computed<number | null>(() => {
  return kpis.value ? calculateTotalCommission(kpis.value.previous) : null
})

function calculateTotalCommission(source: KpiPeriod): number | null {

  const totalGross = source?.total_gross_revenue
  const totalNet = source?.total_net_revenue

  if (totalGross !== undefined && totalNet !== undefined) {
    return totalGross - totalNet
  } else {
    return null
  }

}

</script>
